<template>
  <form class="preferences__form" @submit.prevent="submitPreferences">
    <div class="preferences__id">
      <FormInput
        v-model="userPreferencesTemp.firstName"
        name="firstName"
        :error="firstNameError"
        :label="$t('page.admin-user-profile.first-name')"
        v-validate="'required'"
        has-gray-background
        class="input-first-name"
      />
      <FormInput
        v-model="userPreferencesTemp.lastName"
        name="lastName"
        :error="lastNameError"
        :label="$t('page.admin-user-profile.last-name')"
        v-validate="'required'"
        has-gray-background
      />
    </div>
    <div class="preferences__locale">
      <div class="preferences__label">
        <FormLabel>{{ $t('page.admin-user-profile.language') }}</FormLabel>
      </div>
      <div class="preferences__dropdown">
        <LocaleProvider ref="localeProvider">
          <template slot-scope="{ currentLocale, locales }">
            <BaseSelect
              name="locale"
              :value="currentLocale"
              :items="locales"
              item-text="name"
              item-value="locale"
              :hint="$t('page.admin-user-profile.language-hint')"
              @change="onLocaleChange"
            />
          </template>
        </LocaleProvider>
      </div>
    </div>
    <div class="preferences__time">
      <div class="preferences__label">
        <FormLabel>{{ $t('page.admin-user-profile.time-zone') }}</FormLabel>
      </div>
      <div class="preferences__dropdown">
        <BaseSelect
          name="timezone"
          v-model="selectedTimezone"
          :items="timezoneItems"
          item-text="name"
          item-value="id"
          :hint="$t('page.admin-user-profile.time-zone-hint')"
          @change="onTimezoneChange"
          menuAuto
        />
      </div>
    </div>

    <div class="preferences__actions">
      <FormButton class="preferences__actions-button" isOutlined @click.native="resetForm" :disabled="hasNotChanged">{{
        $t('toast.cancel')
      }}</FormButton>
      <FormButton
        type="submit"
        class="preferences__actions-button"
        :disabled="!isFormValid || hasNotChanged"
        :is-outlined="!isFormValid || hasNotChanged"
        :isLoading="saving"
        >{{ $t('actions.save') }}</FormButton
      >
    </div>
  </form>
</template>

<script>
import { Beacon } from '@/plugins/beacon';
import FormButton from '@/components/form/FormButton.vue';
import FormInput from '@/components/form/FormInput.vue';
import FormLabel from '@/components/form/FormLabel.vue';
import LocaleProvider from '@/components/locale/LocaleProvider';
import BaseSelect from '@/components/base/BaseSelect';
import { timezones } from '@/util/timezones/timezones';

import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'PreferencesForm',
  components: {
    FormButton,
    FormInput,
    FormLabel,
    LocaleProvider,
    BaseSelect,
  },
  props: {
    currentUser: Object,
  },
  data() {
    return {
      valid: true,
      loading: false,
      userPreferencesTemp: {},
    };
  },
  inject: ['$validator'],
  computed: {
    ...mapGetters('userPreferences', ['saving', 'userPreferences']),
    timezoneItems() {
      return timezones;
    },
    selectedTimezone() {
      return timezones.find((timezone) => timezone.id === this.userPreferencesTemp.timezone);
    },
    hasNotChanged() {
      return JSON.stringify(this.currentUser) === JSON.stringify(this.userPreferencesTemp);
    },
    isFormValid() {
      return this.userPreferencesTemp.firstName && this.userPreferencesTemp.lastName;
    },
    firstNameError() {
      const error = this.errors.items.find((x) => x.field === 'firstName');

      return error ? error.msg : undefined;
    },
    lastNameError() {
      const error = this.errors.items.find((x) => x.field === 'lastName');

      return error ? error.msg : undefined;
    },
  },
  methods: {
    ...mapActions('userPreferences', ['updateUserPreferences']),
    onLocaleChange(locale) {
      this.userPreferencesTemp.locale = locale;
    },
    onTimezoneChange(timezone) {
      this.userPreferencesTemp.timezone = timezone;
    },
    submitPreferences() {
      if (this.$validator.validate() && this.isFormValid) {
        this.updateUserPreferences(this.userPreferencesTemp)
          .then(() => {
            Beacon.prefill(this.currentUser);
            this.resetForm();
            this.$refs.localeProvider.changeLocale(this.userPreferences.locale);
            this.$toast.success(this.$t('toast.success'));
          })
          .catch((e) => {
            this.$toast.error(e);
          });
      }
    },
    resetForm() {
      this.userPreferencesTemp = { ...this.currentUser };
      this.$validator.reset();
      this.errors.clear();
    },
  },
  mounted() {
    this.resetForm();
    window.scrollTo({ top: 0 });
  },
};
</script>

<style lang="scss">
.preferences {
  &__form {
    background: $color-white;
    padding: 32px;
    box-shadow: $shadow-default;
    border-radius: 0px 0px $border-radius-root $border-radius-root;
  }

  &__id {
    justify-content: space-between;
    display: flex;
  }

  &__locale,
  &__time {
    display: block;
    align-self: center;
    justify-content: space-between;
  }

  &__locale  {
    margin-bottom: 12px;
  }

  &__time {
    margin: 12px 0px;
  }

  &__dropdown {
    align-self: center;
  }

  &__label {
    align-self: center;
  }

  &__actions {
    justify-content: flex-end;
    display: flex;
    vertical-align: center;
    margin-top: 32px;

    &-button {
      display: flex;
      margin-left: 16px;
    }
  }

  @media screen and (max-width: 468px) {
    &__id {
      display: block;
    }
  }
}

.input-first-name {
  margin-right: 16px;
}
</style>
